import { useMemo } from "react";

import { TIMESTAMP_DISPLAY_DURATION } from "../constants";
import type {
  PreparedPoint,
  PreparedPointBox,
  PreparedPolygon,
  PreparedSpatialSegment,
} from "../components/overlay/types";
import type { TSpatialSegment, TTimelineItem, TTrack, TTrackWaypoint } from "../types";
import { clusterKeyFor, splitWaypointsIntoClusters } from "../utils/tree";

// Re-export prepared annotation types for hook consumers.
export type {
  PreparedPoint,
  PreparedPointBox,
  PreparedPolygon,
  PreparedSpatialSegment,
} from "../components/overlay/types";

const prepareBare = (seg: TSpatialSegment): PreparedSpatialSegment => {
  const endTime = seg.end ?? seg.start;
  const base = {
    id: seg.id,
    startTime: seg.start,
    endTime,
    label: seg.label,
  };
  if (seg.type === "point_box") {
    return { ...base, type: "point_box", coordinates: seg.coordinates } satisfies PreparedPointBox;
  }
  if (seg.type === "point") {
    return { ...base, type: "point", coordinates: seg.coordinates } satisfies PreparedPoint;
  }
  return { ...base, type: "polygon", coordinates: seg.coordinates } satisfies PreparedPolygon;
};

/**
 * One entry per waypoint. `interactionId = clusterId` identifies its timeline
 * cluster; focus resolves it to the owning track. `id` addresses the waypoint.
 */
const prepareWaypoint = (
  wp: TTrackWaypoint,
  track: TTrack,
  index: number,
  clusterId: string,
  nextTime?: number,
): PreparedSpatialSegment => {
  const id = `${track.id}::wp::${index}`;
  const base = {
    id,
    trackId: track.id,
    interactionId: clusterId,
    startTime: wp.time,
    endTime: wp.time,
    displayUntil: Math.min(wp.time + TIMESTAMP_DISPLAY_DURATION, nextTime ?? Infinity),
    label: track.label,
  };
  if (wp.kind === "point_box") {
    return { ...base, type: "point_box", coordinates: wp.coordinates } satisfies PreparedPointBox;
  }
  if (wp.kind === "point") {
    return { ...base, type: "point", coordinates: wp.coordinates } satisfies PreparedPoint;
  }
  return { ...base, type: "polygon", coordinates: wp.coordinates } satisfies PreparedPolygon;
};

const isSpatial = (node: { type: string }): node is TSpatialSegment =>
  node.type === "point_box" || node.type === "point" || node.type === "polygon";

/**
 * - track → one PreparedSpatialSegment per waypoint.
 * - bare spatial → preserve the supplied segment.
 * - collection → recurse.
 * - clip / video → no overlay contribution.
 */
function walkForSpatial(node: TTimelineItem, out: PreparedSpatialSegment[]): void {
  if (node.type === "track") {
    const clusters = splitWaypointsIntoClusters(node.waypoints);
    let globalIdx = 0;
    for (let cIdx = 0; cIdx < clusters.length; cIdx++) {
      const clusterId = clusterKeyFor(node.id, cIdx);
      const cluster = clusters[cIdx]!;
      for (let wpIdx = 0; wpIdx < cluster.length; wpIdx++) {
        out.push(prepareWaypoint(cluster[wpIdx]!, node, globalIdx++, clusterId, cluster[wpIdx + 1]?.time));
      }
    }
    return;
  }
  if (node.type === "collection") {
    for (const child of node.children) {
      if (child.type === "track" || child.type === "collection") {
        walkForSpatial(child, out);
      } else if (isSpatial(child)) {
        out.push(prepareBare(child));
      }
      // clips have no overlay representation; skip
    }
    return;
  }
  if (isSpatial(node)) {
    out.push(prepareBare(node));
    return;
  }
  // video / clip — no overlay contribution
}

/**
 * Extracts all spatial segments (`point_box` | `point` | `polygon`) from the
 * heterogeneous timeline tree and sorts them by `startTime` for efficient
 * visibility lookup.
 */
export const useSpatialSegments = (timelineData: TTimelineItem[]): PreparedSpatialSegment[] => {
  return useMemo(() => {
    const out: PreparedSpatialSegment[] = [];
    for (const item of timelineData) {
      walkForSpatial(item, out);
    }
    out.sort((a, b) => a.startTime - b.startTime);
    return out;
  }, [timelineData]);
};
